/*
 * @Author: liuhongzhuang
 * @Date: 2023-05-20
 * @Description: 基于MUI的轻量级Breadcrumbs组件
 */
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import {
  getSpmPositionCode,
  padSpmPositionIndex,
  useGlobalSpmTerminal,
} from '@/components/Layout/spmTerminal';
import { useStore } from '@/providers/app-store-provider';
import { useSpmDomAttr } from '@/reporting/spm';
import { colors } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  Breadcrumbs as MuiBreadcrumbs,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  Typography,
} from '@mui/material';
import Link from 'next/link';
import React, { cloneElement, type ReactElement, useContext } from 'react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface BreadcrumbsProps extends Omit<MuiBreadcrumbsProps, 'children'> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  className?: string;
  /** spmCnt：商详面包屑坑位编码（breadcrumb_item_XX），由组件按节点序号解析后回传。 */
  SDProductDetailsPageBtnClick?: (btnName: string, spmCnt?: string) => void;
}

/**
 * 循环内挂载点 Data SPM DOM 打标：hook 不能在 map 中调用，封为小组件把 data-spm
 * 克隆到唯一真实子元素上（不新增 DOM 层级）；开关关闭或编码表外时原样渲染，零 DOM 差异。
 */
function SpmDomNode({ code, children }: { code: string | undefined; children: ReactElement }) {
  const domSpm = useSpmDomAttr(code);
  return domSpm ? cloneElement(children, domSpm) : children;
}

const Breadcrumbs = ({
  items,
  separator = '/',
  className = '',
  SDProductDetailsPageBtnClick,
  ...props
}: BreadcrumbsProps) => {
  // const theme = useMuiTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 移动端不显示面包屑导航
  // if (isMobile) return null;
  const { deviceType } = useContext(ResponsiveContext);
  // 全局组件的 B 位跟随当前页面，C/D 沿用全局终端模板。
  // global_pc.breadcrumb 派生自商详 PC 表（breadcrumb_item_01/02）；
  // global_wap 表暂无 breadcrumb 模块，WAP 查表为空照旧不附加。
  const spmPage = useGlobalSpmTerminal(deviceType);
  const getBreadcrumbSpmCnt = (index: number) =>
    getSpmPositionCode(spmPage, 'breadcrumb', `breadcrumb_item_${padSpmPositionIndex(index + 1)}`);
  const waiMode = useStore((state) => state.waiMode);
  // 移动端样式
  let mobileStyleProps = {};
  // pc端样式
  let pcStyleProps = {};
  if (deviceType === EPlatform.WAP) {
    mobileStyleProps = {
      className: 'text-12 font-br',
      sx: {
        '& .MuiBreadcrumbs-separator': {
          mx: 1,
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          fontSize: '3.2vw',
        },
        '& a': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          display: 'block',
          height: '3.733vw',
        },
        '& p': {
          fontSize: '3.2vw',
          fontFamily: 'var(--font-bricolage)',
        },
        '& .breadcrumbs-item': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          fontSize: '3.2vw',
          lineHeight: '3.733vw',
          display: 'block',
        },
        '& .MuiTypography-root': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
          fontSize: '3.2vw',
          lineHeight: '3.733vw',
          display: 'block',
        },
      },
    };
  } else {
    pcStyleProps = {
      sx: {
        '& .MuiBreadcrumbs-separator': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
        '& a': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
        '& .breadcrumbs-item': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
        '& .MuiTypography-root': {
          color: waiMode ? colors.primaryBlack : colors.primaryText,
        },
      },
    };
  }

  return (
    <MuiBreadcrumbs
      separator={separator}
      aria-label="breadcrumb"
      {...mobileStyleProps}
      {...pcStyleProps}
      {...props}
      className={className}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        if (isLast) {
          return (
            <Typography
              key={item.label}
              sx={{ color: waiMode ? colors.primaryBlack : colors.primaryText }}
            >
              {item.label}
            </Typography>
          );
        }

        if (item.onClick) {
          return (
            <SpmDomNode
              key={index}
              code={SDProductDetailsPageBtnClick ? getBreadcrumbSpmCnt(index) : undefined}
            >
              <span
                className="breadcrumbs-item f14 !no-underline font-br text-primary-black hover:text-primary-text cursor-pointer"
                onClick={() => {
                  SDProductDetailsPageBtnClick?.('breadcrumb', getBreadcrumbSpmCnt(index));
                  item.onClick?.();
                }}
              >
                {item.label}
              </span>
            </SpmDomNode>
          );
        }

        return (
          <SpmDomNode
            key={index}
            code={SDProductDetailsPageBtnClick ? getBreadcrumbSpmCnt(index) : undefined}
          >
            <Link
              className="!no-underline"
              href={item.href || '#'}
              onClick={() => {
                SDProductDetailsPageBtnClick?.('breadcrumb', getBreadcrumbSpmCnt(index));
              }}
            >
              <span
                className={`breadcrumbs-item f14 no-underline font-br ${waiMode ? 'text-primary-black' : 'text-auxiliary-text'} hover:text-primary-text hover:underline`}
              >
                {item.label}
              </span>
            </Link>
          </SpmDomNode>
        );
      })}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
